import { useCallback, useEffect, useState } from "react";
import { ListCard } from "../shared";
import useDashboardService from "../services";
import { toast } from "react-toastify";
import {
  DASHBOARD_CLOUD_ACCOUNTS_SORT_OPTIONS as SORT_OPTIONS,
  DEFAULT_CLOUD_ACCOUNTS_SORT as DEFAULT_SORT,
} from "../shared/constants";
import Account from "./account";
import { ICloudAccount } from "../shared/types";
import { useTheme } from "@mui/material/styles";
import { Box, Paper, Skeleton, Typography } from "@mui/material";
import styles from "./cloud_account.module.scss";
import eventEmitter from "../../../components/utils/eventEmitter";
import useFrameworks from "../../../shared/hooks/useFrameworks";

const scrollbarHideStyles = {
  height: "100%",
  overflowY: "auto",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
    width: "0px",
    height: "0px",
    display: "none",
    background: "transparent"
  },
  "&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track": {
    display: "none",
    width: 0
  },
  "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
    display: "none",
    width: 0
  },
  "-ms-overflow-style": "none",
  "& *": {
    scrollbarWidth: "none",
    "-ms-overflow-style": "none"
  }
};

const containerStyles = {
  overflow: "hidden",
  borderRadius: "4px",
  position: "relative",
  height: "100%",
  "& .MuiPaper-root": {
    // borderRadius: "inherit",
    overflow: "hidden",
    height: "100%"
  }
};

const CloudAccount = () => {
  const { fetchCloudAccounts } = useDashboardService();
  const { isFrameworkKeyOn, defaultFrameworkKey } = useFrameworks();
  const [cloudData, setCloudData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState(DEFAULT_SORT);
  const theme = useTheme();
  const [trigger, setTrigger] = useState(0); // Used to re-trigger the effect
  const [nistColumns, setNistColumns] = useState<string[]>([]);
  const [cisColumns, setCisColumns] = useState<string[]>([]);
    const [pcidssColumns, setpcidssColumns] = useState<string[]>([]);
  const [selectedItem, setSelectedItem] = useState<string>('');

  useEffect(() => {
    const handleFilterSaved = () => {
      setLoading(true);
      setTrigger(prev => prev + 1); // Changing this will re-trigger the effect
    };

    eventEmitter.on('filterSaved', handleFilterSaved);

    return () => {
      eventEmitter.off('filterSaved', handleFilterSaved);
    };
  }, []);

  useEffect(() => {
    fetchCloudData();
  }, [sortBy,trigger]);

  // Columns only for frameworks switched on for the organization
  const generateNISTColumns = (data: any[]) => {
    if (!data || data.length === 0) return [];
    const nistKeys = Object.keys(data[0]).filter((key) =>
      key.startsWith("NIST_") && isFrameworkKeyOn(key)
    );
    return nistKeys;
  };
  const generatePCIDSSColumns = (data: any[]) => {
    if (!data || data.length === 0) return [];
    const pcikey = Object.keys(data[0]).filter((key) =>
      key.startsWith("PCI") && isFrameworkKeyOn(key)
    );
    return pcikey;
  };

  const generateCisColumns = (data: any[]) => {
    if (!data || data.length === 0) return [];
    const cisKeys = Object.keys(data[0]).filter((key) =>
      key.startsWith("CIS_") && isFrameworkKeyOn(key)
    );
    return cisKeys;
  };

  const handleColumnSelection = (selectedColumn: string) => {
    setLoading(true);
    setSelectedItem(selectedColumn);
    setTimeout(() => {
      setLoading(false);
    }, 500);
  };

  const fetchCloudData = async () => {
    setLoading(true);
    try {
      // Calls fetchCloudAccounts which invokes DASHBOARD_SCORE via POST with payload: { module: "dashboard-cloud-accounts" }
      const { data, error }: any = await fetchCloudAccounts();

      if (data) {
        setCloudData(data);
        const nistCols = generateNISTColumns(data);
        const cisCols = generateCisColumns(data);
        const pcidssCols = generatePCIDSSColumns(data);
        setpcidssColumns(pcidssCols);
        setNistColumns(nistCols);
        setCisColumns(cisCols);
        
        // Set initial column definitions with first NIST and CIS columns
        if (nistCols.length > 0 && cisCols.length > 0) {
          handleColumnSelection(nistCols[0]);
        }
      }
      if (error) toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderAccountItem = useCallback(
    (item: ICloudAccount, index: number) => {
      // Use the selected column from the column selector
      const selectedCompliance = selectedItem || defaultFrameworkKey;
      console.log('Rendering Account with compliance:', selectedCompliance);
      return <Account key={index} data={item} selectedCompliance={selectedCompliance} />;
    },
    [selectedItem, defaultFrameworkKey]
  );

  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: "4px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          bgcolor: theme.palette.mode === "dark" ? "background.paper" : "white",
          // boxShadow: theme.palette.mode === "dark"
          //   ? "0px 4px 12px rgba(0, 0, 0, 0.3)"
          //   : "0px 4px 12px rgba(0, 0, 0, 0.1)",
          position: "relative",
          overflow: "hidden"
        }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Skeleton variant="text" width={150} height={32} />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: 1 }} />
          </Box>
        </Box>

        {/* List items */}
        {[...Array(5)].map((_, index) => (
          <Box key={index} sx={{ 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center",
            p: 2,
            borderRadius: 1,
            bgcolor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)"
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
              <Skeleton variant="circular" width={40} height={40} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="70%" height={24} />
                <Skeleton variant="text" width="50%" height={16} sx={{ mt: 0.5 }} />
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Skeleton variant="text" width={80} height={24} />
              <Skeleton variant="text" width={80} height={24} />
            </Box>
          </Box>
        ))}
      </Paper>
    );
  }

  return (
    <Box
      data-theme={theme.palette.mode}
      sx={{
        ...scrollbarHideStyles,
        ...containerStyles,
        background: theme.palette.mode === "dark" ? "background.paper" : "white"
      }}
    >
      <ListCard<ICloudAccount>
        title={"Cloud Account"}
        sortOptions={SORT_OPTIONS}
        sortBy={sortBy}
        showSelectColumns={true}
        setSortBy={setSortBy}
        showFilter={true}
        loading={loading}
        data={cloudData}
        columns={[...nistColumns, ...cisColumns, ...pcidssColumns]}
        onColumnSelection={handleColumnSelection}
        renderItem={renderAccountItem}
        children={!loading && (!cloudData || cloudData.length === 0) ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '200px',
            color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'
          }}>
            <Typography variant="body2">No Rows To Show</Typography>
          </Box>
        ) : undefined}
      />
    </Box>
  );
};

export default CloudAccount;
